/**
 * seo.ts · W128.6 · 2026-05-26.
 *
 * Runtime SEO meta-tag updater. A vitrine é SPA sem SSR — o
 * `index.html` traz fallbacks estáticos pro Gênese, mas qualquer
 * outro `<slug>.nullscience.ai` herdaria essa cópia se a gente não
 * sobrescrevesse no client. Crawlers que executam JS (Googlebot
 * moderno, alguns scrapers) leem os meta atualizados; clients que
 * NÃO executam JS (WhatsApp preview, Telegram, iMessage preview)
 * ficam com o fallback estático — a longo prazo o ideal é renderizar
 * meta tags server-side via Worker (out-of-scope desta wave).
 *
 * Idempotente: chama de novo com nova `data` que reescreve. Não
 * cria múltiplas tags — usa querySelector + atributo.
 */

import type { PublicShowcase } from './api';

function projectName(data: PublicShowcase): string {
  return (
    data.heroHeadline?.trim() ||
    data.heroEyebrow?.trim() ||
    data.slug.charAt(0).toUpperCase() + data.slug.slice(1)
  );
}

function projectDescription(data: PublicShowcase): string {
  // Prefer the subheadline (uma linha curativa). Fallback monta uma
  // descrição parametrizada que pelo menos contém localização e
  // dimensões — mostra o resumo certo no card de WhatsApp/Twitter.
  if (data.heroSubheadline?.trim()) return data.heroSubheadline.trim();
  const parts: string[] = [];
  if (data.bedrooms) parts.push(`${data.bedrooms} suítes`);
  if (data.areaSqm) parts.push(`${data.areaSqm} m²`);
  if (data.neighborhood && data.city) {
    parts.push(`${data.neighborhood}, ${data.city}/${data.state ?? ''}`);
  } else if (data.city) {
    parts.push(`${data.city}${data.state ? `/${data.state}` : ''}`);
  }
  return parts.length > 0
    ? `Casa em construção · ${parts.join(' · ')}`
    : 'Casa em construção · acompanhe ao vivo.';
}

function projectLocation(data: PublicShowcase): string | null {
  const parts: string[] = [];
  if (data.neighborhood) parts.push(data.neighborhood);
  if (data.city) parts.push(data.city);
  if (data.state) parts.push(data.state);
  return parts.length > 0 ? parts.join(' / ') : null;
}

function setMeta(selector: string, attr: 'content', value: string | null): void {
  if (typeof document === 'undefined') return;
  const el = document.querySelector<HTMLMetaElement>(selector);
  if (el && value != null) {
    el.setAttribute(attr, value);
  }
}

/**
 * Apply the showcase data to the document head. Mutations:
 *   - `<title>`           → "<projectName> · <location>" or fallback
 *   - `<meta name="description">`
 *   - `<meta property="og:title">`, `og:description`, `og:image`, `og:url`
 *   - `<meta name="twitter:title">`, `twitter:description`, `twitter:image`
 *
 * Skipped silently in tests / non-DOM env.
 */
export function applyShowcaseSeo(data: PublicShowcase): void {
  if (typeof document === 'undefined') return;
  const name = projectName(data);
  const location = projectLocation(data);
  const description = projectDescription(data);
  const url =
    typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/`
      : `https://${data.slug}.nullscience.ai/`;
  // Cover photo é a melhor candidata. Quando ausente, ficamos com a
  // og:image fallback do index.html — não sobrescreve com vazio.
  const image = data.coverPhotoUrl ?? null;

  const title = location ? `${name} · ${location}` : name;
  document.title = title;

  setMeta('meta[name="description"]', 'content', description);
  setMeta('meta[property="og:title"]', 'content', title);
  setMeta('meta[property="og:description"]', 'content', description);
  setMeta('meta[property="og:url"]', 'content', url);
  if (image) setMeta('meta[property="og:image"]', 'content', image);

  setMeta('meta[name="twitter:title"]', 'content', title);
  setMeta('meta[name="twitter:description"]', 'content', description);
  if (image) setMeta('meta[name="twitter:image"]', 'content', image);
}
